import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, User, UserPlus, X, Clock, ChevronRight, GraduationCap, ClipboardList, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from "./AuthContext";
import { authFetch } from "./api";

const API = 'https://hs.aida-advisory.com';

const POSITIONS = ['QB', 'RB', 'FB', 'WR', 'TE', 'OT', 'OG', 'C', 'RE', 'PE', 'DT', 'SAM', 'MIKE', 'WILL', 'CB', 'S', 'K', 'P', 'LS', 'KR', 'PR'];
const CLASS_YEARS = ['Freshman', 'Sophomore', 'Junior', 'Senior'];

const gradeColor = (g) => {
  if (!g) return '#546E7A';
  if (g >= 8) return '#00E676';
  if (g >= 7) return '#00C853';
  if (g >= 6) return '#FFD600';
  if (g >= 4) return '#FFB300';
  return '#EF5350';
};

const gpaColor = (gpa) => {
  if (!gpa) return '#546E7A';
  if (gpa >= 3.5) return '#00E676';
  if (gpa >= 2.5) return '#FFD600';
  return '#EF5350';
};

const tierLabel = (g) => {
  if (!g) return '';
  if (g >= 8) return 'ELITE FIT';
  if (g >= 7) return 'STRONG FIT';
  if (g >= 6) return 'GOOD FIT';
  if (g >= 4) return 'MARGINAL FIT';
  return 'POOR FIT';
};

const PlayerSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [position, setPosition] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [recentPlayers, setRecentPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);

  // Add Prospect Modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPlayer, setNewPlayer] = useState({
    first_name: '',
    last_name: '',
    position: 'RB',
    player_type: 'hs',
    high_school: '',
    hometown: '',
    hs_state: '',
    class_year: 'Senior',
    graduation_year: '',
    gpa: '',
    ncaa_eligibility: 'unknown',
    height: '',
    weight: '',
    dob: '',
  });
  const [creating, setCreating] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Open modal if navigated with state
  useEffect(() => {
    if (user?.role === 'super_admin') {
      authFetch(`${API}/api/schools/`).then(r => r.ok ? r.json() : []).then(setSchools);
    }
  }, []);
  useEffect(() => {
    if (location.state?.openAddPlayer) setShowAddModal(true);
  }, [location.state]);

  // Fetch recently scouted
  useEffect(() => {
    const fetchRecent = async () => {
      try {
        const res = await authFetch(`${API}/api/grades/recent`);
        if (res.ok) setRecentPlayers(await res.json());
      } catch {}
    };

    // "Recently scouted" is grading activity, so a prospect who has been added
    // but never graded appeared nowhere until you searched their exact name.
    // The roster is the honest default for this screen.
    const fetchAll = async () => {
      try {
        const res = await authFetch(`${API}/api/players/`);
        if (res.ok) setAllPlayers(await res.json());
      } catch {}
    };
    fetchAll();
    fetchRecent();
  }, []);

  const checkDuplicate = async (firstName, lastName, highSchool, position) => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (fullName.length < 3 || !highSchool.trim()) { setDuplicateWarning(null); return; }
    try {
      const res = await authFetch(`${API}/api/players/search?q=${encodeURIComponent(fullName)}`);
      if (!res.ok) return;
      const results = await res.json();
      const match = results.find(p =>
        p.name.toLowerCase() === fullName.toLowerCase() &&
        p.high_school?.toLowerCase() === highSchool.trim().toLowerCase() &&
        p.position?.toUpperCase() === position?.toUpperCase()
      );
      setDuplicateWarning(match || null);
    } catch {}
  };

  const handleDelete = async (playerId, playerName) => {
    if (!window.confirm(`Delete ${playerName}? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API}/api/players/${playerId}`, { method: 'DELETE' });
      if (res.ok) {
        setSearchResults(prev => prev.filter(p => p.id !== playerId));
        setRecentPlayers(prev => prev.filter(p => p.id !== playerId));
      } else { alert('Failed to delete player.'); }
    } catch { alert('Failed to delete player.'); }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setHasSearched(true);
    try {
      // HS version: search local database only
      let url = `${API}/api/players/search?q=${encodeURIComponent(searchTerm)}`;
      if (position) url += `&position=${position}`;
      const res = await authFetch(url);
      const data = await res.json();
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlayer = async () => {
    if (!newPlayer.first_name.trim() || !newPlayer.last_name.trim()) { alert('First and last name are required'); return; }
    if (!newPlayer.position) { alert('Position is required'); return; }

    setCreating(true);
    try {
      const fullName = `${newPlayer.first_name.trim()} ${newPlayer.last_name.trim()}`.trim();
      const payload = {
        name: fullName,
        position: newPlayer.position,
        player_type: 'hs',
        high_school: newPlayer.high_school || null,
        hometown: newPlayer.hometown || null,
        hs_state: newPlayer.hs_state || null,
        class_year: newPlayer.class_year || null,
        graduation_year: newPlayer.graduation_year ? parseInt(newPlayer.graduation_year) : null,
        gpa: newPlayer.gpa ? parseFloat(newPlayer.gpa) : null,
        ncaa_eligibility: newPlayer.ncaa_eligibility || 'unknown',
        height: newPlayer.height || null,
        weight: newPlayer.weight ? parseInt(newPlayer.weight) : null,
        dob: newPlayer.dob || null,
        school_id: newPlayer.school_id ? parseInt(newPlayer.school_id) : null,
      };

      const response = await authFetch(`${API}/api/players/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const created = await response.json();
        setShowAddModal(false);
        setNewPlayer({
          first_name: '', last_name: '', position: 'RB', player_type: 'hs',
          high_school: '', hometown: '', hs_state: '',
          class_year: 'Senior', graduation_year: '',
          gpa: '', ncaa_eligibility: 'unknown',
          height: '', weight: '', dob: '', school_id: ''
        });
        navigate(`/players/${created.id}/fit`);
      } else {
        const error = await response.json();
        alert(`Failed to create prospect: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      alert('Failed to create prospect. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-6 md:pt-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 md:mb-2">Prospect Search</h1>
            <p className="text-sm md:text-base text-[#B0BEC5]">Search your prospect database by name or position</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center justify-center gap-2 flex-shrink-0">
            <UserPlus size={18} /> Add Prospect
          </button>
        </div>

        {/* Search Bar */}
        <div className="panel-card mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-end">
            <div className="flex-1">
              <label className="block text-[#B0BEC5] text-sm mb-2">Prospect Name</label>
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search by name..."
                  className="w-full bg-[#243B55] text-white pl-10 pr-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
                />
              </div>
            </div>
            <div className="w-full md:w-40">
              <label className="block text-[#B0BEC5] text-sm mb-2">Position</label>
              <select
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className="w-full bg-[#243B55] text-white px-3 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
              >
                <option value="">All Positions</option>
                {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <button onClick={handleSearch} disabled={loading} className="btn-primary md:px-8 py-3 disabled:opacity-50 flex-shrink-0">
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Recently Scouted */}
        {!hasSearched && recentPlayers.length > 0 && (
          <div className="panel-card mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={18} className="text-[#00D4FF]" />
              <h2 className="text-white font-bold">Recently Scouted</h2>
            </div>
            <div className="space-y-2">
              {recentPlayers.filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i).map((p) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/players/${p.id}/fit`)}
                  className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#243B55] cursor-pointer transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <ChevronRight size={14} className="text-[#546E7A] group-hover:text-[#00D4FF]" />
                    <span className="text-white text-sm">{p.name}</span>
                    <span className="text-[#546E7A] text-xs">{p.position}</span>
                    {p.high_school && <span className="text-[#546E7A] text-xs">• {p.high_school}</span>}
                  </div>
                  {p.fit_score && (
                    <div className="flex items-center gap-2">
                      <span className="font-rajdhani font-bold" style={{ color: gradeColor(p.fit_score) }}>
                        {p.fit_score.toFixed(1)}
                      </span>
                      <span className="text-xs" style={{ color: gradeColor(p.fit_score) }}>
                        {tierLabel(p.fit_score)}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full roster, shown until a search narrows it */}
        {!hasSearched && allPlayers.length > 0 && (
          <div className="panel-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold">
                All Prospects <span className="text-[#546E7A] font-normal text-sm">({allPlayers.length})</span>
              </h2>
            </div>
            <div className="space-y-2">
              {allPlayers.map((p) => (
                <div
                  key={p.id}
                  onClick={() => navigate(`/players/${p.id}/fit?tab=profile`)}
                  className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg
                             hover:bg-[#243B55] cursor-pointer transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <ChevronRight size={14} className="text-[#546E7A] group-hover:text-[#00D4FF] flex-shrink-0" />
                    <span className="text-white text-sm truncate">{p.name}</span>
                    <span className="text-[#00D4FF] text-xs font-semibold flex-shrink-0">{p.position}</span>
                    {p.class_year && <span className="text-[#546E7A] text-xs hidden sm:inline">{p.class_year}</span>}
                    {p.high_school && <span className="text-[#546E7A] text-xs truncate hidden md:inline">• {p.high_school}</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {p.gpa != null && (
                      <span className="font-rajdhani font-bold text-sm" style={{ color: gpaColor(p.gpa) }}>
                        {Number(p.gpa).toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {hasSearched && (
          <div className="panel-card">
            <h2 className="text-white font-bold mb-4">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </h2>

            {searchResults.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-[#B0BEC5] mb-2">No prospects found for "{searchTerm}"</div>
                <div className="text-[#546E7A] text-sm mb-4">Try a different name or add them manually</div>
                <button onClick={() => setShowAddModal(true)} className="btn-primary flex items-center gap-2 mx-auto">
                  <UserPlus size={16} /> Add Prospect
                </button>
              </div>
            ) : (
              <>
              {/* Desktop: table. Mobile: the card list below. */}
              <table className="w-full desktop-only">
                <thead>
                  <tr className="border-b border-[#243B55]">
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase">Name</th>
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase">Pos</th>
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase">Class</th>
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase">High School</th>
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase flex items-center gap-1">
                      <GraduationCap size={12} /> GPA
                    </th>
                    <th className="text-left py-2 px-3 text-[#546E7A] text-xs uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((player) => (
                    <tr key={player.id} className="border-b border-[#243B55]/50 last:border-0 hover:bg-[#243B55]/30">
                      <td className="py-3 px-3 text-white text-sm font-medium">{player.name}</td>
                      <td className="py-3 px-3 text-[#B0BEC5] text-sm">{player.position}</td>
                      <td className="py-3 px-3 text-[#B0BEC5] text-sm">{player.class_year || '—'}</td>
                      <td className="py-3 px-3 text-[#B0BEC5] text-sm">{player.high_school || '—'}</td>
                      <td className="py-3 px-3">
                        <span className="font-rajdhani font-bold" style={{ color: gpaColor(player.gpa) }}>
                          {player.gpa ? player.gpa.toFixed(2) : '—'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/players/${player.id}/fit?tab=profile`)}
                            className="bg-[#243B55] text-[#00D4FF] px-3 py-1.5 rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-[#37474F] transition-colors border border-[#00D4FF]/30"
                          >
                            <User size={14} /> View
                          </button>
                          <button
                            onClick={() => navigate(`/players/${player.id}/fit?tab=grading`)}
                            className="bg-[#00D4FF] text-[#0A0E27] px-3 py-1.5 rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-[#4FC3F7] transition-colors"
                          >
                            <ClipboardList size={14} /> Evaluate
                          </button>
                          <button
                            onClick={() => handleDelete(player.id, player.name)}
                            className="p-1.5 rounded-lg border border-[#546E7A]/30 text-[#546E7A] hover:border-[#EF5350]/60 hover:text-[#EF5350] transition-colors"
                            title="Delete player"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile: each prospect is a tappable card, not a squeezed row */}
              <div className="mobile-only space-y-3">
                {searchResults.map((player) => (
                  <div key={player.id} className="list-card">
                    <div
                      onClick={() => navigate(`/players/${player.id}/fit?tab=profile`)}
                      className="flex items-start justify-between gap-3 cursor-pointer"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold truncate">{player.name}</div>
                        <div className="text-[#B0BEC5] text-xs mt-1 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[#00D4FF] font-semibold">{player.position}</span>
                          {player.class_year && <span>• {player.class_year}</span>}
                          {player.high_school && <span className="truncate">• {player.high_school}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-rajdhani font-bold text-xl leading-none" style={{ color: gpaColor(player.gpa) }}>
                          {player.gpa ? player.gpa.toFixed(2) : '—'}
                        </div>
                        <div className="text-[#546E7A] text-[10px] uppercase tracking-wide mt-1">GPA</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-[#546E7A]/20">
                      <button
                        onClick={() => navigate(`/players/${player.id}/fit?tab=profile`)}
                        className="flex-1 bg-[#243B55] text-[#00D4FF] px-3 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 border border-[#00D4FF]/30"
                      >
                        <User size={14} /> View
                      </button>
                      <button
                        onClick={() => navigate(`/players/${player.id}/fit?tab=grading`)}
                        className="flex-1 bg-[#00D4FF] text-[#0A0E27] px-3 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
                      >
                        <ClipboardList size={14} /> Evaluate
                      </button>
                      <button
                        onClick={() => handleDelete(player.id, player.name)}
                        className="px-3 rounded-lg border border-[#546E7A]/30 text-[#546E7A] flex items-center justify-center"
                        aria-label={`Delete ${player.name}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              </>
            )}
          </div>
        )}

        {/* ═══════════════ ADD PROSPECT MODAL ═══════════════ */}
        {showAddModal && (
          <div className="sheet-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
            <div className="sheet panel-card max-w-2xl w-full max-h-[90vh] overflow-y-auto scroll-y">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-white text-xl md:text-2xl font-bold">Add New Prospect</h2>
                <button onClick={() => { setShowAddModal(false); setDuplicateWarning(null); }} className="text-[#B0BEC5] hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">

                {duplicateWarning && (
                  <div className="rounded-lg border border-[#FFB300]/40 bg-[#FFB300]/10 p-4 flex items-start gap-3">
                    <AlertTriangle size={18} className="text-[#FFB300] flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="text-[#FFB300] font-semibold text-sm mb-1">Duplicate Found</div>
                      <div className="text-[#B0BEC5] text-sm mb-2">
                        <strong>{duplicateWarning.name}</strong> ({duplicateWarning.position}) at {duplicateWarning.high_school} already exists.
                      </div>
                      <button
                        onClick={() => { setShowAddModal(false); setDuplicateWarning(null); navigate(`/players/${duplicateWarning.id}/fit`); }}
                        className="text-[#00D4FF] text-sm font-semibold hover:underline"
                      >
                        Go to existing record →
                      </button>
                    </div>
                  </div>
                )}

                {/* Name + Position */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">First Name *</label>
                    <input
                      type="text"
                      value={newPlayer.first_name}
                      onChange={(e) => { const v = e.target.value; setNewPlayer(p => ({...p, first_name: v})); checkDuplicate(v, newPlayer.last_name, newPlayer.high_school, newPlayer.position); }}
                      placeholder="First"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Last Name *</label>
                    <input
                      type="text"
                      value={newPlayer.last_name}
                      onChange={(e) => { const v = e.target.value; setNewPlayer(p => ({...p, last_name: v})); checkDuplicate(newPlayer.first_name, v, newPlayer.high_school, newPlayer.position); }}
                      placeholder="Last"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Position *</label>
                    <select
                      value={newPlayer.position}
                      onChange={(e) => { const v = e.target.value; setNewPlayer(p => ({...p, position: v})); checkDuplicate(newPlayer.first_name, newPlayer.last_name, newPlayer.high_school, v); }}
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
                    >
                      {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                {/* Class Year + Graduation Year */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Class Year</label>
                    <select
                      value={newPlayer.class_year}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, class_year: e.target.value }))}
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none"
                    >
                      {CLASS_YEARS.map(cy => <option key={cy} value={cy}>{cy}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Graduation Year</label>
                    <input type="number" value={newPlayer.graduation_year}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, graduation_year: e.target.value }))}
                      placeholder="2026"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                </div>

                {/* School Assignment for Super Admin */}
                {user?.role === 'super_admin' && (
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Assign to School <span className="text-red-400">*</span></label>
                    <select value={newPlayer.school_id || ''} onChange={(e) => setNewPlayer(prev => ({ ...prev, school_id: e.target.value }))}
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none">
                      <option value="">Select a school...</option>
                      {schools.map(s => <option key={s.id} value={s.id}>{s.name} ({s.city}, {s.state})</option>)}
                    </select>
                  </div>
                )}
                {/* School Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">High School</label>
                    <input type="text" value={newPlayer.high_school}
                      onChange={(e) => { const v = e.target.value; setNewPlayer(p => ({...p, high_school: v})); checkDuplicate(newPlayer.first_name, newPlayer.last_name, v, newPlayer.position); }}
                      placeholder="Lincoln High School"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Hometown</label>
                    <input type="text" value={newPlayer.hometown}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, hometown: e.target.value }))}
                      placeholder="City, State"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                </div>

                {/* GPA + NCAA Eligibility — highlighted */}
                <div className="rounded-lg border border-[#00E676]/30 bg-[#00E676]/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap size={16} className="text-[#00E676]" />
                    <span className="text-[#00E676] font-semibold text-sm">NCAA Academic Info</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#B0BEC5] text-sm mb-2">GPA</label>
                      <input
                        type="number" step="0.01" min="0" max="4.0"
                        value={newPlayer.gpa}
                        onChange={(e) => setNewPlayer(prev => ({ ...prev, gpa: e.target.value }))}
                        placeholder="3.75"
                        className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#00E676]/40 focus:border-[#00E676] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[#B0BEC5] text-sm mb-2">NCAA Eligibility</label>
                      <select
                        value={newPlayer.ncaa_eligibility}
                        onChange={(e) => setNewPlayer(prev => ({ ...prev, ncaa_eligibility: e.target.value }))}
                        className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#00E676]/40 focus:border-[#00E676] focus:outline-none"
                      >
                        <option value="unknown">Unknown</option>
                        <option value="eligible">Eligible</option>
                        <option value="at_risk">At Risk</option>
                        <option value="ineligible">Ineligible</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Measurables */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Height</label>
                    <input type="text" value={newPlayer.height}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, height: e.target.value }))}
                      placeholder="6-2"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-[#B0BEC5] text-sm mb-2">Weight (lbs)</label>
                    <input type="number" value={newPlayer.weight}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, weight: e.target.value }))}
                      placeholder="185"
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-[#B0BEC5] text-sm mb-2">Date of Birth</label>
                    <input type="date" value={newPlayer.dob}
                      onChange={(e) => setNewPlayer(prev => ({ ...prev, dob: e.target.value }))}
                      className="w-full bg-[#243B55] text-white px-4 py-3 rounded-lg border border-[#546E7A]/30 focus:border-[#00D4FF] focus:outline-none" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => { setShowAddModal(false); setDuplicateWarning(null); }} className="btn-secondary flex-1">Cancel</button>
                <button
                  onClick={handleCreatePlayer}
                  disabled={creating || !newPlayer.first_name.trim() || !newPlayer.last_name.trim()}
                  className="btn-primary flex-1 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Add Prospect'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PlayerSearch;
